import type { CheckoutLegalLinksConfig } from "./checkoutConfig";

export type LegalConsentLocale = "et" | "en";

export type LegalConsentSegment =
  | { type: "text"; value: string }
  | { type: "link"; value: string; href: string };

export type LegalConsentPhrase = {
  segments: LegalConsentSegment[];
};

type PolicyItem = {
  label: string;
  href: string;
};

const translations = {
  et: {
    prefix: "Andmete salvestamiseks tuleb nõustuda ",
    terms: "kasutustingimustega",
    cookie: "küpsistepoliitikaga",
    privacy: "privaatsuspoliitikaga",
    conjunction: " ja ",
  },
  en: {
    prefix: "To save your data, you have to agree with ",
    terms: "terms of use",
    cookie: "cookie policy",
    privacy: "privacy policy",
    conjunction: " and ",
  },
} as const;

function appendPolicyItem(
  segments: LegalConsentSegment[],
  item: PolicyItem | { label: string; href?: string }
): void {
  if (item.href) {
    segments.push({ type: "link", value: item.label, href: item.href });
  } else {
    segments.push({ type: "text", value: item.label });
  }
}

export function buildLegalConsentPhrase(
  locale: LegalConsentLocale,
  legalLinks: CheckoutLegalLinksConfig
): LegalConsentPhrase {
  const t = translations[locale] ?? translations.et;
  const termsUrl = legalLinks.termsUrl?.trim();
  const cookieUrl = legalLinks.cookiePolicyUrl?.trim();
  const privacyUrl = legalLinks.privacyPolicyUrl?.trim();

  const items: PolicyItem[] = [];
  if (termsUrl) {
    items.push({ label: t.terms, href: termsUrl });
  }
  if (cookieUrl) {
    items.push({ label: t.cookie, href: cookieUrl });
  }
  if (privacyUrl) {
    items.push({ label: t.privacy, href: privacyUrl });
  }

  const segments: LegalConsentSegment[] = [{ type: "text", value: t.prefix }];

  if (items.length === 0) {
    segments.push({ type: "text", value: t.terms });
    segments.push({ type: "text", value: "." });
    return { segments };
  }

  items.forEach((item, index) => {
    if (index === 0) {
      appendPolicyItem(segments, item);
      return;
    }

    if (index === items.length - 1 && items.length === 3) {
      segments.push({ type: "text", value: t.conjunction });
    } else {
      segments.push({ type: "text", value: ", " });
    }

    appendPolicyItem(segments, item);
  });

  segments.push({ type: "text", value: "." });

  return { segments };
}
